import { createSelector } from 'reselect'
import { getTourV2IdForCheckoutItem, isLuxPlusSubscriptionItem, isSubscriptionJoinItem } from 'lib/checkout/checkoutUtils'
import { getCanRedeemLuxPlusBenefits, getIsFreePreviewLuxPlusMember, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'

/**
 * Membership-end evidence for cart cleanup.
 *
 * Chosen boundaries, pending product and svc-membership confirmation:
 * - Browser refresh / cart ready / visibility. Not an offline membership-end event.
 * - Own default carts only. Shared and trip-planner carts are not auto-deleted.
 * - Paid EXPIRED on a subscription revision accepted by the strict refresh, or a finished free preview with no current membership.
 * - CANCELLED is not treated as ended until svc-membership confirms benefits stop immediately.
 * - ACTIVE, AWAITING_RENEWAL, DO_NOT_RENEW, region mismatch, disableMembership, 404, loading, and errors do not delete.
 * - Replacing the subscription invalidates evidence from an earlier strict refresh.
 * - Only an active LuxPlus join item exempts that cart. A saved join does not.
 * - One browser notification per end event, stored locally. Cross-device acknowledgement is not guaranteed.
 */
export interface LuxPlusMembershipEndEvidence {
  hasEnded: boolean
  eventId?: string
}

const NO_MEMBERSHIP_END: LuxPlusMembershipEndEvidence = { hasEnded: false }

export const getLuxPlusMembershipEndEvidence = createSelector(
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => state.auth.account.memberId,
  (state: App.State) => state.auth.account.luxPlus.member.disableMembership,
  (state: App.State) => state.auth.account.luxPlus.member.cartEligibilityRefresh,
  (state: App.State) => state.auth.account.luxPlus.member.subscription.item,
  (state: App.State) => state.auth.account.luxPlus.member.subscription.lastFreePreviews,
  (state: App.State) => state.auth.account.luxPlus.member.subscriptionRevision,
  (state: App.State) => state.geo.currentRegionCode,
  (isLuxPlusEnabled, memberId, disableMembership, refresh, subscriptionItem, lastFreePreviews, subscriptionRevision, currentRegionCode): LuxPlusMembershipEndEvidence => {
    if (
      !isLuxPlusEnabled ||
      disableMembership ||
      !memberId ||
      refresh?.status !== 'success' ||
      refresh.memberId !== memberId ||
      refresh.regionCode !== currentRegionCode ||
      refresh.subscriptionRevision === undefined ||
      refresh.subscriptionRevision !== subscriptionRevision
    ) {
      return NO_MEMBERSHIP_END
    }

    if (subscriptionItem && subscriptionItem.region !== currentRegionCode) {
      return NO_MEMBERSHIP_END
    }

    if (subscriptionItem?.status === 'ACTIVE' || subscriptionItem?.status === 'AWAITING_RENEWAL') {
      return NO_MEMBERSHIP_END
    }

    if (subscriptionItem?.status === 'CANCELLED') {
      return NO_MEMBERSHIP_END
    }

    if (
      subscriptionItem &&
      subscriptionItem.tier !== 'free_preview' &&
      subscriptionItem.status === 'EXPIRED'
    ) {
      return {
        hasEnded: true,
        eventId: `paid:${subscriptionItem.id}:${subscriptionItem.status}:${subscriptionItem.expiryDate}`,
      }
    }

    if (subscriptionItem) {
      return NO_MEMBERSHIP_END
    }

    const regionalPreviews = (lastFreePreviews ?? []).filter(preview => preview.region === currentRegionCode)
    if (regionalPreviews.some(preview => preview.status === 'ACTIVE')) {
      return NO_MEMBERSHIP_END
    }

    const expiredPreviews = regionalPreviews
      .filter(preview => preview.status === 'EXPIRED')
      .sort((previewA, previewB) => previewA.endDate.localeCompare(previewB.endDate))
    const latestExpiredPreview = expiredPreviews[expiredPreviews.length - 1]
    if (!latestExpiredPreview) {
      return NO_MEMBERSHIP_END
    }

    return {
      hasEnded: true,
      eventId: `preview:${latestExpiredPreview.region}:${latestExpiredPreview.endDate}:${latestExpiredPreview.status}`,
    }
  },
)

export type LuxPlusMemberOnlyCheckoutBlock =
  | { blocked: false }
  | { blocked: true, reason: 'ineligible' | 'unconfirmed' | 'membershipRequired' }

function getCheckoutItemOfferId(item: App.Checkout.AnyItem): string | undefined {
  if (item.itemType === 'tourV2') return getTourV2IdForCheckoutItem(item)
  if ('offerId' in item && typeof item.offerId === 'string') return item.offerId
  return undefined
}

function checkoutItemCanBeMemberOnly(item: App.Checkout.AnyItem): boolean {
  return item.itemType === 'hotel' ||
    item.itemType === 'bedbankHotel' ||
    item.itemType === 'accommodationV2' ||
    item.itemType === 'villa' ||
    item.itemType === 'tourV2' ||
    item.itemType === 'cruise' ||
    item.itemType === 'experience'
}

const EMPTY_CHECKOUT_JOIN_PROTECTED_ITEM_IDS: ReadonlySet<string> = new Set()

/**
 * Item ids in the current checkout that are part of an in-progress LuxPlus join
 * with a member-only offer. Only those cart rows are kept. Other carts and saved
 * items stay eligible for cleanup. Leaving checkout clears the protection even if
 * the checkout cart is still in Redux.
 */
export function getLuxPlusCheckoutJoinProtectedItemIds(
  state: App.State,
  offersById: Record<string, App.AnyOffer | undefined>,
  isCheckoutRoute: boolean,
): ReadonlySet<string> {
  if (!isCheckoutRoute) return EMPTY_CHECKOUT_JOIN_PROTECTED_ITEM_IDS

  const items = state.checkout.cart.items
  const isJoining = items.some(item => isLuxPlusSubscriptionItem(item) || isSubscriptionJoinItem(item))
  if (!isJoining) return EMPTY_CHECKOUT_JOIN_PROTECTED_ITEM_IDS

  const protectedItemIds = new Set<string>()
  items.forEach(item => {
    if (!checkoutItemCanBeMemberOnly(item)) return
    const offerId = getCheckoutItemOfferId(item)
    if (!offerId || !isMemberOnlyOffer(offersById[offerId])) return
    if ('itemId' in item && typeof item.itemId === 'string') protectedItemIds.add(item.itemId)
  })

  if (protectedItemIds.size === 0) return EMPTY_CHECKOUT_JOIN_PROTECTED_ITEM_IDS
  return protectedItemIds
}

export function isMemberOnlyOffer(offer: App.AnyOffer | undefined): offer is App.AnyOffer {
  return !!offer && 'luxPlus' in offer && offer.luxPlus?.access === 'memberOnly'
}

function canFreePreviewBuyMemberOnlyOffers(state: App.State, offers: Array<App.AnyOffer>): boolean {
  if (!getIsFreePreviewLuxPlusMember(state) || offers.length === 0) return false
  return offers.every(offer => isMemberOnlyOffer(offer) && 'luxPlus' in offer && offer.luxPlus?.restrictPurchaseToMembers === false)
}

function isMembershipRestrictedOffer(offer: App.AnyOffer | undefined): offer is App.AnyOffer {
  return !!offer && 'luxPlus' in offer && (offer.luxPlus?.access === 'memberOnly' || offer.luxPlus?.access === 'earlyAccess')
}

export function checkoutCartHasLoadedMembershipRestrictedOffer(
  state: App.State,
  offersById: Record<string, App.AnyOffer | undefined>,
): boolean {
  return state.checkout.cart.items.some(item => {
    if (!checkoutItemCanBeMemberOnly(item)) return false
    const offerId = getCheckoutItemOfferId(item)
    if (!offerId) return false
    return isMembershipRestrictedOffer(offersById[offerId])
  })
}

export function checkoutCartHasUnresolvedMemberCapableOffer(
  state: App.State,
  offersById: Record<string, App.AnyOffer | undefined>,
): boolean {
  return state.checkout.cart.items.some(item => {
    if (!checkoutItemCanBeMemberOnly(item)) return false
    const offerId = getCheckoutItemOfferId(item)
    if (!offerId) return false
    return !offersById[offerId]
  })
}

/**
 * Final checkout gate. Uses the strict refresh already written into state.
 * LuxPlus-disabled brands are never blocked. When LuxPlus is enabled, a loaded member-only or early-access
 * offer or a member-capable item whose offer is not loaded can block payment.
 * A loaded unrestricted offer does not. Keeps spoofed sessions, an in-cart LuxPlus join,
 * and the active-preview unrestricted exception.
 * `ineligible` means membership has ended. Other missing purchase eligibility uses `membershipRequired`.
 */
export function getLuxPlusMemberOnlyCheckoutBlock(
  state: App.State,
  offersById: Record<string, App.AnyOffer | undefined>,
): LuxPlusMemberOnlyCheckoutBlock {
  if (state.checkout.cart.existingOrder || !getIsLuxPlusEnabled(state)) {
    return { blocked: false }
  }

  const items = state.checkout.cart.items
  const loadedRestrictedOffers = items.flatMap(item => {
    if (!checkoutItemCanBeMemberOnly(item)) return []
    const offerId = getCheckoutItemOfferId(item)
    if (!offerId) return []
    const offer = offersById[offerId]
    return isMembershipRestrictedOffer(offer) ? [offer] : []
  })
  const hasUnresolvedOffer = checkoutCartHasUnresolvedMemberCapableOffer(state, offersById)
  if (loadedRestrictedOffers.length === 0 && !hasUnresolvedOffer) {
    return { blocked: false }
  }

  if (state.auth.account.isSpoofed) {
    return { blocked: false }
  }

  if (items.some(item => isLuxPlusSubscriptionItem(item) || isSubscriptionJoinItem(item))) {
    return { blocked: false }
  }

  if (!state.auth.account.memberId) {
    return { blocked: true, reason: 'membershipRequired' }
  }

  const member = state.auth.account.luxPlus.member
  const refresh = member.cartEligibilityRefresh
  if (
    (refresh?.status !== 'success' && refresh?.status !== 'no-subscription') ||
    refresh.memberId !== state.auth.account.memberId ||
    refresh.regionCode !== state.geo.currentRegionCode ||
    refresh.subscriptionRevision === undefined ||
    refresh.subscriptionRevision !== member.subscriptionRevision
  ) {
    return { blocked: true, reason: 'unconfirmed' }
  }

  if (getCanRedeemLuxPlusBenefits(state) && !hasUnresolvedOffer) {
    return { blocked: false }
  }

  if (canFreePreviewBuyMemberOnlyOffers(state, loadedRestrictedOffers) && !hasUnresolvedOffer) {
    return { blocked: false }
  }

  if (hasUnresolvedOffer) {
    return { blocked: true, reason: 'unconfirmed' }
  }

  if (getLuxPlusMembershipEndEvidence(state).hasEnded) {
    return { blocked: true, reason: 'ineligible' }
  }

  return { blocked: true, reason: 'membershipRequired' }
}
